import { Color, DefaultLoader, Engine, ExcaliburGraphicsContext, Scene, Timer, vec, Vector } from "excalibur";
import { createTowerAtCenter, tower } from "./tower";
import { Enemy, EnemyType, statsFor, liveEnemies } from "./enemy";
import { gameState } from "./gameState";

function randomEdgeSpawn(engine: Engine): Vector {
  // Spawn on the edges of the drawable rectangle centered at (0,0)
  const side = Math.floor(Math.random() * 4);
  const margin = 20;
  const w = engine.drawWidth;
  const h = engine.drawHeight;
  const halfW = w / 2;
  const halfH = h / 2;
  switch (side) {
    case 0: return vec((Math.random() * w) - halfW, -halfH - margin); // top edge
    case 1: return vec(halfW + margin, (Math.random() * h) - halfH); // right edge
    case 2: return vec((Math.random() * w) - halfW, halfH + margin); // bottom edge
    case 3: return vec(-halfW - margin, (Math.random() * h) - halfH); // left edge
  }
  return vec(0, 0);
}

export class MyLevel extends Scene {

  isSpawning = false;
  timeSinceLastSpawn = 0;
  spawnIntervalMs = 600; // ms between enemies in a wave
  enemiesLeftInWave = 0;
  awaitingNextWave = false;

  override onInitialize(engine: Engine): void {
    // Create the one tower in the center
    const t = createTowerAtCenter(engine);
    this.add(t);

    // Ensure camera is centered on world origin (0,0)
    this.camera.pos = vec(0, 0);

    // Start a first wave
    this.startNextWave(engine);
  }

  private startNextWave(_engine: Engine) {
    // Do not start new waves if the tower is dead
    if (tower.isKilled()) {
      return;
    }

    // Reset waiting flag now that we're starting
    this.awaitingNextWave = false;

    try { gameState.saveToStorage(); } catch {}

    gameState.wave += 1;
    // Persist progress whenever wave increments
    // Design wave composition
    const wave = gameState.wave;
    const baseHP = this.baseHP + wave * 4;
    const baseMoney = this.baseMoney + wave * 2;

    // number of each type; ensure at least some normals
    const countNormal = 6 + wave;
    const countHeavy = Math.floor(wave / 3);
    const countRanged = Math.floor(wave / 2);
    const countFast = Math.floor(wave / 2);
    const hasBoss = wave % 10 === 0; // every 10th wave has a boss

    this.pendingSpawns = [];

    for (let i = 0; i < countNormal; i++) this.pendingSpawns.push('normal');
    for (let i = 0; i < countHeavy; i++) this.pendingSpawns.push('heavy');
    for (let i = 0; i < countRanged; i++) this.pendingSpawns.push('ranged');
    for (let i = 0; i < countFast; i++) this.pendingSpawns.push('fast');
    if (hasBoss) this.pendingSpawns.push('boss');

    // shuffle spawns
    for (let i = this.pendingSpawns.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.pendingSpawns[i], this.pendingSpawns[j]] = [this.pendingSpawns[j], this.pendingSpawns[i]];
    }

    this.baseHP = baseHP;
    this.baseMoney = baseMoney;
    this.isSpawning = true;
    this.enemiesLeftInWave = this.pendingSpawns.length;
    this.timeSinceLastSpawn = 0;

    // announce wave (no "starting..." text)
    gameState.waveStatus = '';
  }

  private pendingSpawns: EnemyType[] = [];
  private baseHP = 5;
  private baseMoney = 5;

  override onPreLoad(_loader: DefaultLoader): void {
    // Add any scene-specific resources to load
  }

  override onPreUpdate(engine: Engine, elapsedMs: number): void {

    // handle spawning
    if (this.isSpawning) {
      this.timeSinceLastSpawn += elapsedMs;
      if (this.timeSinceLastSpawn >= this.spawnIntervalMs && this.pendingSpawns.length > 0) {
        this.timeSinceLastSpawn = 0;
        const type = this.pendingSpawns.shift()!;
        const stats = statsFor(type, this.baseHP, this.baseMoney);
        const pos = randomEdgeSpawn(engine);
        const e = new Enemy(type, stats, pos);
        this.add(e);
      }
      if (this.pendingSpawns.length === 0) {
        this.isSpawning = false;
        // No "in progress" text
        gameState.waveStatus = '';
      }
    } else {
      // check if wave complete (no enemies remain) using liveEnemies registry for O(1)
      const remaining = liveEnemies.size;
      if (remaining === 0 && this.pendingSpawns.length === 0) {
        gameState.waveStatus = 'cleared';
        // start next after a short delay using a one-shot Timer; guard against scheduling multiple times
        if (!this.awaitingNextWave) {
          this.awaitingNextWave = true;
          const timer = new Timer({ interval: 1500, repeats: false, action: () => this.startNextWave(engine) });
          this.add(timer);
          timer.start();
        }
      }
    }

    // Check defeat
    if (tower.isKilled()) {
      gameState.waveStatus = 'game over';
      // Stop further waves
      this.pendingSpawns = [];
      this.isSpawning = false;
    }
  }

  override onPostUpdate(_engine: Engine, _elapsedMs: number): void {
    // Called after everything updates in the scene
  }

  override onPreDraw(ctx: ExcaliburGraphicsContext, _elapsedMs: number): void {
    // Draw game state UI
  }
  override onPostDraw(ctx: ExcaliburGraphicsContext, _elapsedMs: number): void {
    // Draw tower attack range on top of everything so it is always visible
    if (tower && !tower.isKilled()) {
      const effectiveRange = tower.range + gameState.upgrades.rangeBonus;
      const fill = Color.fromRGB(80, 160, 255, 0.10);
      const stroke = Color.fromRGB(80, 160, 255, 0.9);
      // Convert world origin (0,0) to screen coordinates to align with Scene post-draw context
      const screenPos = this.engine.screen.worldToScreenCoordinates(vec(0, 0));
      ctx.drawCircle(screenPos, effectiveRange, fill, stroke, 2);
    }
  }
}